import { useState } from 'react';
import {
    Button,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { Icon } from '../../../shared/Icon';
import { DateField } from '../DateField';
import { DeletePublicationDialog } from './DeletePublicationDialog';

import { PublicationDraftsState } from './usePublicationDrafts';

interface Props {
    state: PublicationDraftsState;
    isNewGift: boolean;
    canUpdate: boolean;
    canDelete: boolean;
}

interface DateCellProps {
    label: string;
    value: Date;
    onChange: (value: Date | null) => void;
}

const compactDateSx = {
    '& .MuiPickersInputBase-root': { fontSize: '0.8em' },
    '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
};

const DateCell = ({ label, value, onChange }: DateCellProps) => (
    <TableCell sx={{ minWidth: 150 }}>
        <DateField
            required
            hideLabel
            label={label}
            value={value}
            onChange={onChange}
            sx={compactDateSx}
        />
    </TableCell>
);

export const PublicationsSection = ({
    state,
    isNewGift,
    canUpdate,
    canDelete,
}: Props) => {
    const [publicationToDelete, setPublicationToDelete] = useState<string>();

    return (
        <>
            <Divider />
            <Stack spacing={1}>
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Publications
                    </Typography>
                    {!isNewGift && (
                        <Button
                            type="button"
                            size="small"
                            startIcon={<Icon name="add" />}
                            disabled={state.drafts.some((item) => !item.id)}
                            onClick={state.add}
                        >
                            Add publication
                        </Button>
                    )}
                </Stack>
                {state.error && (
                    <Typography color="error" variant="body2" role="alert">
                        {state.error}
                    </Typography>
                )}
                <TableContainer
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                    <Table
                        size="small"
                        aria-label="Gift publications"
                        sx={{ '& .MuiTableCell-root': { px: 1 } }}
                    >
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    Starts
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    Ends
                                </TableCell>
                                <TableCell align="right" sx={{ width: 120 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.drafts.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        align="center"
                                        sx={{ color: 'text.secondary', py: 2 }}
                                    >
                                        No publications scheduled
                                    </TableCell>
                                </TableRow>
                            )}
                            {state.drafts.map((publication, index) => (
                                <TableRow
                                    key={publication.id ?? `new-${index}`}
                                >
                                    <DateCell
                                        label="Starts"
                                        value={new Date(publication.startDate)}
                                        onChange={(startDate) => {
                                            if (startDate) {
                                                state.updateDraft(index, {
                                                    startDate,
                                                });
                                            }
                                        }}
                                    />
                                    <DateCell
                                        label="Ends"
                                        value={publication.endDate || ''}
                                        onChange={(endDate) => {
                                            if (endDate) {
                                                state.updateDraft(index, {
                                                    endDate,
                                                });
                                            }
                                        }}
                                    />
                                    <TableCell
                                        align="right"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {publication.id ? (
                                            <>
                                                {canUpdate && (
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        disabled={
                                                            state.savingId ===
                                                            publication.id
                                                        }
                                                        onClick={() =>
                                                            state.update(index)
                                                        }
                                                    >
                                                        {state.savingId ===
                                                        publication.id
                                                            ? '…'
                                                            : 'Save'}
                                                    </Button>
                                                )}
                                                {canDelete && (
                                                    <IconButton
                                                        type="button"
                                                        size="small"
                                                        color="error"
                                                        aria-label="Remove publication"
                                                        onClick={() =>
                                                            setPublicationToDelete(
                                                                publication.id,
                                                            )
                                                        }
                                                    >
                                                        <Icon name="delete" />
                                                    </IconButton>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    type="button"
                                                    size="small"
                                                    disabled={state.savingNew}
                                                    onClick={() =>
                                                        state.create(index)
                                                    }
                                                >
                                                    {state.savingNew
                                                        ? 'Saving…'
                                                        : 'Publish'}
                                                </Button>
                                                <IconButton
                                                    type="button"
                                                    size="small"
                                                    aria-label="Discard publication"
                                                    onClick={() =>
                                                        state.discard(index)
                                                    }
                                                >
                                                    <Icon name="close" />
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
            <DeletePublicationDialog
                open={publicationToDelete !== undefined}
                onClose={() => setPublicationToDelete(undefined)}
                onConfirm={() => {
                    const id = publicationToDelete;
                    setPublicationToDelete(undefined);
                    if (id) state.remove(id);
                }}
            />
        </>
    );
};
